import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { GeneratedCover, paintedCover } from './CoverArt.tsx';
import { useScrollLock } from '../scrollLock.ts';

/**
 * Cover art. Real artwork wins when the scanner found any; otherwise a piece
 * is generated from the title (see CoverArt.tsx) so a shelf of untagged
 * recordings still reads as a shelf of distinct things.
 *
 * Real covers load progressively: a 32px version (a few hundred bytes) shows
 * blurred at once under a slow sheen, then the sized cover resolves over it
 * from soft to sharp. Nothing pops in from nothing. A cover the browser
 * already has lands sharp on the first frame, with no fade to sit through.
 */
export function Cover({
  coverId,
  title,
  creator,
  className,
  size = 320,
}: {
  coverId: string | null;
  title: string;
  creator?: string;
  className?: string;
  /** Pixel width to fetch: 320 for shelves, 640 for a detail page, 1024 for the player. */
  size?: 320 | 640 | 1024;
}) {
  const [state, setState] = useState<'loading' | 'loaded' | 'failed'>('loading');
  const [instant, setInstant] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const img = imgRef.current;
    if (img?.complete && img.naturalWidth > 0) {
      setInstant(true);
      setState('loaded');
    } else {
      setInstant(false);
      setState('loading');
    }
  }, [coverId, size]);

  if (coverId && state !== 'failed') {
    const base = `/api/media/asset/${coverId}`;
    return (
      <div
        className={`cover cover-progressive${state === 'loaded' ? ' is-loaded' : ''}${
          instant ? ' is-instant' : ''
        } ${className ?? ''}`}
      >
        <img
          className="cover-lqip"
          src={`${base}?w=32`}
          alt=""
          aria-hidden="true"
          decoding="async"
        />
        <img
          ref={imgRef}
          className="cover-full"
          src={`${base}?w=${size}`}
          alt=""
          loading="lazy"
          decoding="async"
          width={size}
          height={size}
          onLoad={() => setState('loaded')}
          onError={() => setState('failed')}
        />
      </div>
    );
  }
  return <PaintedCover seed={`${creator ?? ''}/${title}`} title={title} className={className} />;
}

/**
 * A cover for something with none of its own: a painted piece in the logo's
 * style, soft-to-sharp like a real one, with the title set over its dark lower
 * third. Falls back to the drawn arcs if the image cannot load.
 */
function PaintedCover({
  seed,
  title,
  className,
}: {
  seed: string;
  title: string;
  className?: string;
}) {
  const art = paintedCover(seed);
  const [state, setState] = useState<'loading' | 'loaded' | 'failed'>('loading');
  const ref = useRef<HTMLImageElement>(null);
  useEffect(() => {
    const img = ref.current;
    setState(img?.complete && img.naturalWidth > 0 ? 'loaded' : 'loading');
  }, [art.src]);
  if (state === 'failed') {
    return (
      <div className={`cover cover-generated ${className ?? ''}`} aria-hidden="true">
        <GeneratedCover seed={seed} />
        <span className="cover-title">{title}</span>
      </div>
    );
  }
  return (
    <div
      className={`cover cover-generated cover-painted cover-progressive${state === 'loaded' ? ' is-loaded' : ''}${
        art.mirror ? ' is-mirrored' : ''
      } ${className ?? ''}`}
      aria-hidden="true"
    >
      <img className="cover-lqip" src={art.lqip} alt="" decoding="async" />
      <img
        ref={ref}
        className="cover-full"
        src={art.src}
        alt=""
        loading="lazy"
        decoding="async"
        width={640}
        height={640}
        onLoad={() => setState('loaded')}
        onError={() => setState('failed')}
      />
    </div>
  );
}

export function EmptyState({
  title,
  children,
  action,
  art,
}: {
  title: string;
  children?: ReactNode;
  action?: ReactNode;
  /** Name of an illustration under /art (e.g. 'empty-library'); decorative. */
  art?: string;
}) {
  return (
    <div className={`empty${art ? ' empty-illustrated' : ''}`}>
      {art && (
        <img className="empty-art" src={`/art/${art}.webp`} alt="" width={640} height={640} />
      )}
      <h3>{title}</h3>
      {children && <p>{children}</p>}
      {action}
    </div>
  );
}

export function ErrorNote({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="error-note" role="alert">
      {message}
      {onRetry && (
        <>
          {' '}
          <button className="btn btn-sm btn-quiet" onClick={onRetry}>
            Try again
          </button>
        </>
      )}
    </div>
  );
}

/**
 * A page that is still fetching: its real heading at once, soft placeholders
 * where the content will land - so a tap always shows the page it opened.
 */
export function PageSkeleton({
  title,
  lede,
  grid = false,
}: {
  title: string;
  lede?: string;
  grid?: boolean;
}) {
  return (
    <>
      <div className="page-head">
        <h1>{title}</h1>
        {lede && <p className="lede">{lede}</p>}
      </div>
      {grid ? (
        <SkeletonGrid count={6} />
      ) : (
        <div className="skeleton-stack" aria-hidden="true">
          <div className="skeleton" style={{ height: 88 }} />
          <div className="skeleton" style={{ height: 88 }} />
          <div className="skeleton" style={{ height: 88 }} />
        </div>
      )}
    </>
  );
}

export function SkeletonGrid({ count = 8 }: { count?: number }) {
  return (
    <div className="card-grid" aria-hidden="true">
      {Array.from({ length: count }, (_, i) => (
        <div key={i}>
          <div className="skeleton" style={{ aspectRatio: '1' }} />
          <div className="skeleton" style={{ marginTop: 10, width: '80%' }} />
          <div className="skeleton" style={{ marginTop: 6, width: '50%' }} />
        </div>
      ))}
    </div>
  );
}

/**
 * Modal sheet with focus management: focus moves in on open, is trapped,
 * Escape closes, and focus returns to the opener on close.
 */
const SHEET_EXIT_MS = 240;

/**
 * A sheet: rises from the bottom on a phone (a centred card on a desktop).
 *
 * The header - grab handle, title, close - stays still; only the body
 * scrolls, and it never drags the page behind it. Drag the header down, or
 * pull the body down while it is at its top, and the sheet follows the finger;
 * let go far or fast enough and it slides away, otherwise it settles back.
 * Closing (the X, the scrim, Escape, a drag) always plays the exit before the
 * parent unmounts it.
 */
export function Sheet({
  title,
  onClose,
  children,
  labelId,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  labelId?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const scrimRef = useRef<HTMLDivElement>(null);
  const id = labelId ?? 'sheet-title';
  const [closing, setClosing] = useState(false);
  const closingRef = useRef(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useScrollLock(true);

  /** Play the way out, then let the parent unmount us. */
  const requestClose = useCallback(() => {
    if (closingRef.current) return;
    closingRef.current = true;
    setClosing(true);
    window.setTimeout(() => onCloseRef.current(), SHEET_EXIT_MS);
  }, []);

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const node = ref.current;
    if (!node) return;
    const focusables = () =>
      [
        ...node.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        ),
      ].filter((el) => !el.hasAttribute('disabled'));
    // Focus the sheet itself on touch screens: focusing the first field would
    // pop the keyboard up over a sheet the person only opened to look at.
    const touch = window.matchMedia('(pointer: coarse)').matches;
    (touch ? node : (focusables()[0] ?? node)).focus({ preventScroll: true });
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        requestClose();
      } else if (e.key === 'Tab') {
        const els = focusables();
        if (els.length === 0) return;
        const first = els[0] as HTMLElement;
        const last = els[els.length - 1] as HTMLElement;
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    node.addEventListener('keydown', onKey);
    return () => {
      node.removeEventListener('keydown', onKey);
      opener?.focus({ preventScroll: true });
    };
  }, [requestClose]);

  // Drag to dismiss. Native listeners: a pull on the body must be able to
  // cancel the browser's own scroll, which needs a non-passive touchmove.
  useEffect(() => {
    const sheet = ref.current;
    const body = bodyRef.current;
    if (!sheet || !body) return;
    let startY = 0;
    let lastY = 0;
    let lastT = 0;
    let velocity = 0;
    let dragging = false;
    let fromBody = false;
    let armed = false;

    const move = (dy: number) => {
      const y = Math.max(0, dy);
      sheet.style.transform = `translate3d(0, ${y}px, 0)`;
      if (scrimRef.current) {
        scrimRef.current.style.opacity = String(Math.max(0, 1 - y / (sheet.offsetHeight * 0.9)));
      }
    };
    const begin = (y: number) => {
      startY = y;
      lastY = y;
      lastT = performance.now();
      velocity = 0;
      armed = true;
      dragging = false;
    };
    const track = (y: number) => {
      const now = performance.now();
      velocity = (y - lastY) / Math.max(1, now - lastT);
      lastY = y;
      lastT = now;
    };
    const end = () => {
      if (!dragging) {
        armed = false;
        return;
      }
      dragging = false;
      armed = false;
      const dy = lastY - startY;
      // A finger that paused before lifting was not flicking.
      if (performance.now() - lastT > 90) velocity = 0;
      sheet.classList.remove('dragging');
      if (dy > Math.min(140, sheet.offsetHeight * 0.3) || velocity > 0.55) {
        // Carry on from where the finger left it.
        sheet.style.transition = `transform ${SHEET_EXIT_MS}ms cubic-bezier(0.32, 0.72, 0, 1)`;
        sheet.style.transform = 'translate3d(0, 110%, 0)';
        if (scrimRef.current) {
          scrimRef.current.style.transition = `opacity ${SHEET_EXIT_MS}ms ease`;
          scrimRef.current.style.opacity = '0';
        }
        closingRef.current = true;
        window.setTimeout(() => onCloseRef.current(), SHEET_EXIT_MS);
      } else {
        sheet.style.transition = 'transform 320ms cubic-bezier(0.32, 0.72, 0, 1)';
        sheet.style.transform = '';
        if (scrimRef.current) {
          scrimRef.current.style.transition = 'opacity 320ms ease';
          scrimRef.current.style.opacity = '';
        }
        window.setTimeout(() => {
          sheet.style.transition = '';
          if (scrimRef.current) scrimRef.current.style.transition = '';
        }, 330);
      }
    };

    // Header: always drags.
    const head = sheet.querySelector<HTMLElement>('.sheet-grab-zone');
    const onHeadDown = (e: PointerEvent) => {
      if ((e.target as HTMLElement).closest('button, a, input')) return;
      if (closingRef.current) return;
      head?.setPointerCapture(e.pointerId);
      begin(e.clientY);
      fromBody = false;
    };
    const onHeadMove = (e: PointerEvent) => {
      if (!armed || fromBody) return;
      track(e.clientY);
      if (!dragging && Math.abs(e.clientY - startY) > 4) {
        dragging = true;
        sheet.classList.add('dragging');
      }
      if (dragging) move(e.clientY - startY);
    };
    head?.addEventListener('pointerdown', onHeadDown);
    head?.addEventListener('pointermove', onHeadMove);
    head?.addEventListener('pointerup', end);
    head?.addEventListener('pointercancel', end);

    // Body: a pull down while scrolled to the top becomes a drag.
    const onTouchStart = (e: TouchEvent) => {
      if (closingRef.current || e.touches.length !== 1) return;
      fromBody = true;
      begin(e.touches[0]!.clientY);
      armed = body.scrollTop <= 0;
    };
    const onTouchMove = (e: TouchEvent) => {
      if (!armed || !fromBody) return;
      const y = e.touches[0]!.clientY;
      const dy = y - startY;
      if (!dragging) {
        if (dy < -2 || body.scrollTop > 0) {
          armed = false; // scrolling the content, not the sheet
          return;
        }
        if (dy > 6) {
          const t = e.target as HTMLElement;
          // Sliders, text selection and drag grips keep their own gestures.
          if (t.closest('.slider, textarea, .reorder-grip, [data-no-sheet-drag]')) {
            armed = false;
            return;
          }
          dragging = true;
          sheet.classList.add('dragging');
          startY = y;
        }
      }
      if (dragging) {
        e.preventDefault();
        track(y);
        move(y - startY);
      }
    };
    body.addEventListener('touchstart', onTouchStart, { passive: true });
    body.addEventListener('touchmove', onTouchMove, { passive: false });
    body.addEventListener('touchend', end);
    body.addEventListener('touchcancel', end);
    return () => {
      head?.removeEventListener('pointerdown', onHeadDown);
      head?.removeEventListener('pointermove', onHeadMove);
      head?.removeEventListener('pointerup', end);
      head?.removeEventListener('pointercancel', end);
      body.removeEventListener('touchstart', onTouchStart);
      body.removeEventListener('touchmove', onTouchMove);
      body.removeEventListener('touchend', end);
      body.removeEventListener('touchcancel', end);
    };
  }, []);

  // Portalled to <body>: a page's entrance animation makes it the containing
  // block for anything position: fixed inside it, which parked sheets mid-page
  // and let them inherit the page's alignment.
  return createPortal(
    <>
      <div className={`scrim${closing ? ' closing' : ''}`} onClick={requestClose} ref={scrimRef} />
      <div
        className={`sheet${closing ? ' closing' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={id}
        ref={ref}
        tabIndex={-1}
        onAnimationEnd={(e) => {
          // The entrance is done: drop it, so toggling classes later (a drag)
          // can never replay it over the finger's position.
          if (e.target === e.currentTarget && !closingRef.current) {
            e.currentTarget.classList.add('settled');
          }
        }}
      >
        <div className="sheet-grab-zone">
          <span className="sheet-grab" aria-hidden="true" />
          <div className="sheet-head">
            <h2 id={id}>{title}</h2>
            <button className="icon-btn" onClick={requestClose} aria-label="Close">
              <Icon name="x" />
            </button>
          </div>
        </div>
        <div className="sheet-body" ref={bodyRef}>
          {children}
        </div>
      </div>
    </>,
    document.body,
  );
}

/** One inline icon set, stroke 1.5, currentColor. */
const PATHS: Record<string, ReactNode> = {
  library: (
    <>
      <path d="M4 4.5h5.5v15H4zM9.5 4.5H15v15H9.5z" />
      <path d="m15.2 5.4 4.6 1.2-3.6 13.5-4.5-1.2" />
    </>
  ),
  plans: (
    <>
      <rect x="3.5" y="5" width="17" height="15.5" rx="2" />
      <path d="M3.5 9.5h17M8 3v4M16 3v4" />
    </>
  ),
  journal: (
    <>
      <path d="M5 4.5A1.5 1.5 0 0 1 6.5 3h11A1.5 1.5 0 0 1 19 4.5v15a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 5 19.5z" />
      <path d="M8.5 3v18M12 8h4M12 11.5h4" />
    </>
  ),
  stats: <path d="M4 20V10M9.3 20V4M14.6 20v-9M20 20V7" />,
  sources: (
    <>
      <rect x="3" y="6" width="18" height="12.5" rx="3" />
      <path d="m10.2 9.5 4.6 2.75-4.6 2.75z" />
    </>
  ),
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.1 14.4a1.5 1.5 0 0 0 .3 1.65l.05.06a1.8 1.8 0 1 1-2.55 2.55l-.06-.06a1.5 1.5 0 0 0-1.65-.3 1.5 1.5 0 0 0-.91 1.37v.17a1.8 1.8 0 1 1-3.6 0v-.09a1.5 1.5 0 0 0-.98-1.37 1.5 1.5 0 0 0-1.65.3l-.06.06A1.8 1.8 0 1 1 4.44 16.3l.06-.06a1.5 1.5 0 0 0 .3-1.65 1.5 1.5 0 0 0-1.37-.91h-.17a1.8 1.8 0 1 1 0-3.6h.09a1.5 1.5 0 0 0 1.37-.98 1.5 1.5 0 0 0-.3-1.65l-.06-.06A1.8 1.8 0 1 1 6.91 4.84l.06.06a1.5 1.5 0 0 0 1.65.3h.07a1.5 1.5 0 0 0 .91-1.37v-.17a1.8 1.8 0 1 1 3.6 0v.09a1.5 1.5 0 0 0 .91 1.37 1.5 1.5 0 0 0 1.65-.3l.06-.06a1.8 1.8 0 1 1 2.55 2.55l-.06.06a1.5 1.5 0 0 0-.3 1.65v.07a1.5 1.5 0 0 0 1.37.91h.17a1.8 1.8 0 1 1 0 3.6h-.09a1.5 1.5 0 0 0-1.37.91z" />
    </>
  ),
  plug: (
    <>
      <path d="M9 3.5V8M15 3.5V8M7 8h10v3a5 5 0 0 1-10 0z" />
      <path d="M12 16v4.5" />
    </>
  ),
  play: <path d="M8 5.5v13l10-6.5z" fill="currentColor" stroke="none" />,
  pause: <path d="M8.5 5.5v13M15.5 5.5v13" strokeWidth="2.4" />,
  next: <path d="m6 6 7 6-7 6zM17.5 6v12" />,
  prev: <path d="m18 6-7 6 7 6zM6.5 6v12" />,
  x: <path d="m6 6 12 12M18 6 6 18" />,
  plus: <path d="M12 5v14M5 12h14" />,
  check: <path d="m5 12.5 4.5 4.5L19 7.5" />,
  bell: (
    <>
      <path d="M12 4a6 6 0 0 1 6 6v3.5l1.5 2.5H4.5L6 13.5V10a6 6 0 0 1 6-6z" />
      <path d="M10 19a2 2 0 0 0 4 0" />
    </>
  ),
  moon: <path d="M20 14.5A8.5 8.5 0 0 1 9.5 4 8.5 8.5 0 1 0 20 14.5z" />,
  mic: (
    <>
      <rect x="9" y="3.5" width="6" height="11" rx="3" />
      <path d="M5.5 11.5a6.5 6.5 0 0 0 13 0M12 18v3" />
    </>
  ),
  trash: <path d="M5 7h14M10 7V4.5h4V7M7 7l1 13h8l1-13M10.5 10.5v6M13.5 10.5v6" />,
  doc: (
    <>
      <path d="M6.5 3.5h7L18 8v12.5h-11.5z" />
      <path d="M13 3.5V8h4.5" />
    </>
  ),
  expand: <path d="M4 9V4h5M20 15v5h-5M4 4l6 6M20 20l-6-6" />,
  history: (
    <>
      <path d="M4.5 12a7.5 7.5 0 1 1 2.2 5.3M4.5 12H2M4.5 12l1.6 2.6" />
      <path d="M12 8v4.5l3 1.8" />
    </>
  ),
  external: <path d="M9 5h10v10M19 5l-9.5 9.5M11 5H5v14h14v-6" />,
  timer: (
    <>
      <circle cx="12" cy="13.5" r="7.5" />
      <path d="M12 9.5v4l2.5 1.5M9.5 2.5h5M19 6.5l1.5-1.5" />
    </>
  ),
  sun: (
    <>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 3v2.2M12 18.8V21M3 12h2.2M18.8 12H21M5.6 5.6l1.6 1.6M16.8 16.8l1.6 1.6M18.4 5.6l-1.6 1.6M7.2 16.8l-1.6 1.6" />
    </>
  ),
  heart: (
    <path d="M12 20s-7.5-4.6-7.5-9.4A4.1 4.1 0 0 1 12 8a4.1 4.1 0 0 1 7.5 2.6C19.5 15.4 12 20 12 20z" />
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="6.5" />
      <path d="m16 16 4.5 4.5" />
    </>
  ),
  github: (
    <path
      d="M12 2.5a9.5 9.5 0 0 0-3 18.5c.5.1.65-.2.65-.45v-1.7c-2.65.6-3.2-1.15-3.2-1.15-.45-1.1-1.05-1.4-1.05-1.4-.85-.6.05-.6.05-.6.95.05 1.45 1 1.45 1 .85 1.45 2.2 1.05 2.75.8.1-.6.35-1.05.6-1.3-2.1-.25-4.3-1.05-4.3-4.7 0-1.05.35-1.9 1-2.55-.1-.25-.45-1.2.1-2.5 0 0 .8-.25 2.6 1a9 9 0 0 1 4.75 0c1.8-1.25 2.6-1 2.6-1 .55 1.3.2 2.25.1 2.5.65.65 1 1.5 1 2.55 0 3.65-2.2 4.45-4.3 4.7.35.3.65.85.65 1.75v2.6c0 .25.15.55.65.45A9.5 9.5 0 0 0 12 2.5z"
      fill="currentColor"
      stroke="none"
    />
  ),
  'chevron-right': <path d="m9 6 6 6-6 6" />,
  'chevron-left': <path d="m15 6-6 6 6 6" />,
  'chevron-up': <path d="m6 15 6-6 6 6" />,
  breath: <path d="M4 9.5h9.5a2.5 2.5 0 1 0-2.5-2.5M4 13.5h13a2.5 2.5 0 1 1-2.5 2.5M4 17.5h6" />,
  lotus: (
    <path d="M12 19c-4.5 0-8-2.2-8.5-5.5 2.6.2 5 1.3 6.3 3M12 19c4.5 0 8-2.2 8.5-5.5-2.6.2-5 1.3-6.3 3M12 19c-2.2-1.6-3.3-4-3.3-6.6S10 7.6 12 5.5c2 2.1 3.3 4.3 3.3 6.9S14.2 17.4 12 19z" />
  ),
  book: (
    <path d="M4.5 5.5c2.5-1 5-1 7.5.8v13c-2.5-1.8-5-1.8-7.5-.8zM19.5 5.5c-2.5-1-5-1-7.5.8v13c2.5-1.8 5-1.8 7.5-.8z" />
  ),
  talk: <path d="M4.5 5.5h15v10h-8l-4.5 3.5v-3.5H4.5zM8.5 9.5h7M8.5 12h4.5" />,
  waves: (
    <path d="M3.5 9c2.2-2 4.3-2 6.4 0s4.3 2 6.4 0 3.2-1.4 4.2-.8M3.5 14c2.2-2 4.3-2 6.4 0s4.3 2 6.4 0 3.2-1.4 4.2-.8" />
  ),
  video: <path d="M4 7h11v10H4zM15 10.5l5-3v9l-5-3" />,
  'check-circle': (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="m8.3 12.3 2.6 2.6 5-5.3" />
    </>
  ),
  circle: <circle cx="12" cy="12" r="8.5" />,
  folder: (
    <path d="M3.5 7.5a2 2 0 0 1 2-2h4l2 2.5h7a2 2 0 0 1 2 2v7.5a2 2 0 0 1-2 2h-13a2 2 0 0 1-2-2z" />
  ),
  eye: (
    <>
      <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  'eye-off': (
    <path d="M4 4l16 16M9.9 5.8A9.7 9.7 0 0 1 12 5.5c6 0 9.5 6.5 9.5 6.5a17 17 0 0 1-2.9 3.7M6.3 7.4A16.6 16.6 0 0 0 2.5 12S6 18.5 12 18.5a9.6 9.6 0 0 0 4.3-1M9.9 10a3 3 0 0 0 4.1 4.1" />
  ),
  sliders: <path d="M4 7h9M17 7h3M4 17h3M11 17h9M15 5v4M9 15v4" />,
  list: (
    <path d="M9 6.5h11M9 12h11M9 17.5h11M4.5 6.5h.01M4.5 12h.01M4.5 17.5h.01" strokeWidth="1.8" />
  ),
  pencil: (
    <path d="M14.5 5.5l4 4M4.5 19.5l1-4.5L15.8 4.7a1.4 1.4 0 0 1 2 0l1.5 1.5a1.4 1.4 0 0 1 0 2L9 18.5z" />
  ),
  flag: <path d="M5.5 20.5v-16M5.5 4.5h11.2l-2.2 4 2.2 4H5.5" />,
  grip: (
    <g fill="currentColor" stroke="none">
      <circle cx="9" cy="6.5" r="1.5" />
      <circle cx="15" cy="6.5" r="1.5" />
      <circle cx="9" cy="12" r="1.5" />
      <circle cx="15" cy="12" r="1.5" />
      <circle cx="9" cy="17.5" r="1.5" />
      <circle cx="15" cy="17.5" r="1.5" />
    </g>
  ),
  reorder: <path d="M8 19.5v-15M4.5 8 8 4.5 11.5 8M16 4.5v15M12.5 16l3.5 3.5 3.5-3.5" />,
  gauge: <path d="M4.5 16.5a8 8 0 1 1 15 0M12 13l4-4.5M12 13.2h.01" />,
  volume: (
    <path d="M4.5 9.5h3l4.5-4v13l-4.5-4h-3zM16 9a4 4 0 0 1 0 6M18.5 6.5a7.5 7.5 0 0 1 0 11" />
  ),
  'volume-low': <path d="M4.5 9.5h3l4.5-4v13l-4.5-4h-3zM16 9.5a3.5 3.5 0 0 1 0 5" />,
  sprout: (
    <path d="M12 20v-8M12 12c0-3.5-2.5-6-6.5-6 0 3.5 2.5 6 6.5 6zM12 14c0-3 2-5.5 6-5.5 0 3-2 5.5-6 5.5z" />
  ),
  more: <path d="M6 12h.01M12 12h.01M18 12h.01" strokeWidth="2.6" />,
  grid: <path d="M4.5 4.5h6v6h-6zM13.5 4.5h6v6h-6zM4.5 13.5h6v6h-6zM13.5 13.5h6v6h-6z" />,
  'skip-back': (
    <>
      <path d="M4.5 12a7.5 7.5 0 1 0 2.2-5.3M4.5 4v3.5H8" />
      <text
        x="12"
        y="15.2"
        textAnchor="middle"
        fontSize="7.2"
        fontWeight="600"
        fill="currentColor"
        stroke="none"
      >
        15
      </text>
    </>
  ),
  'skip-fwd': (
    <>
      <path d="M19.5 12a7.5 7.5 0 1 1-2.2-5.3M19.5 4v3.5H16" />
      <text
        x="12"
        y="15.2"
        textAnchor="middle"
        fontSize="7.2"
        fontWeight="600"
        fill="currentColor"
        stroke="none"
      >
        30
      </text>
    </>
  ),
  'chevron-down': <path d="m6 9 6 6 6-6" />,
  shield: (
    <>
      <path d="M12 3.2 5 5.8v5.4c0 4.4 2.9 8.1 7 9.6 4.1-1.5 7-5.2 7-9.6V5.8z" />
      <path d="m9.2 12 2 2 3.6-3.8" />
    </>
  ),
  download: (
    <>
      <path d="M12 4v11M7.5 10.5 12 15l4.5-4.5" />
      <path d="M5 19.5h14" />
    </>
  ),
  'on-device': (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5v7M9 11.5l3 3 3-3" />
    </>
  ),
  friends: (
    <>
      <circle cx="9" cy="8.5" r="3.2" />
      <path d="M3.5 19.5c.6-3.2 2.8-5 5.5-5s4.9 1.8 5.5 5" />
      <circle cx="16.5" cy="9.5" r="2.6" />
      <path d="M15.2 14.6c.4-.1.9-.1 1.3-.1 2.3 0 4.1 1.5 4.6 4.3" />
    </>
  ),
  'user-plus': (
    <>
      <circle cx="10" cy="8.5" r="3.4" />
      <path d="M3.5 19.5c.6-3.3 3.1-5.2 6.5-5.2 1.3 0 2.5.3 3.5.8M18.5 13v6M15.5 16h6" />
    </>
  ),
  link: (
    <>
      <path d="M10 14a4 4 0 0 0 5.66 0l3-3a4 4 0 0 0-5.66-5.66l-1 1" />
      <path d="M14 10a4 4 0 0 0-5.66 0l-3 3a4 4 0 0 0 5.66 5.66l1-1" />
    </>
  ),
  copy: (
    <>
      <rect x="8.5" y="8.5" width="11" height="11" rx="2" />
      <path d="M15.5 8.5V6.5a2 2 0 0 0-2-2h-7a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h2" />
    </>
  ),
  share: (
    <>
      <path d="M12 3.5v11M8 7.5l4-4 4 4" />
      <path d="M6 11.5H5a1.5 1.5 0 0 0-1.5 1.5v6A1.5 1.5 0 0 0 5 20.5h14a1.5 1.5 0 0 0 1.5-1.5v-6A1.5 1.5 0 0 0 19 11.5h-1" />
    </>
  ),
  key: (
    <>
      <circle cx="8" cy="15" r="3.8" />
      <path d="M10.8 12.3 19.5 3.5M16 7l2.5 2.5M13.8 9.2l2 2" />
    </>
  ),
  restart: (
    <>
      <path d="M4.5 12a7.5 7.5 0 1 0 2.2-5.3L4.5 9" />
      <path d="M4.5 4.5V9H9" />
    </>
  ),
  sparkle: (
    <path d="M12 3.5 13.8 9l5.7 1.8-5.7 1.8L12 18.3 10.2 12.6 4.5 10.8 10.2 9zM18.5 4v3M20 5.5h-3" />
  ),
};

export function Icon({ name, size = 18 }: { name: string; size?: number }) {
  return (
    <svg
      className="nav-ic"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ inlineSize: size, blockSize: size }}
    >
      {PATHS[name] ?? null}
    </svg>
  );
}

/** An on/off switch. A real button with role="switch", so it reads and keys right. */
export function Switch({
  checked,
  onChange,
  label,
  disabled = false,
  busy = false,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  disabled?: boolean;
  busy?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      aria-busy={busy || undefined}
      disabled={disabled || busy}
      className={`switch${checked ? ' on' : ''}${busy ? ' busy' : ''}`}
      onClick={() => onChange(!checked)}
    >
      <span className="switch-knob" />
    </button>
  );
}

/**
 * A slider built for fingers. The native range input on iOS only moves when
 * the touch lands on its small thumb, and a value re-rendered while playback
 * runs can drop a grab that has just begun. Here the whole strip (taller than
 * it looks) takes the touch: the thumb jumps to the finger and follows it,
 * `onInput` previews, and `onCommit` fires once on release. While a drag is on,
 * the slider shows the drag, whatever `value` the parent keeps sending.
 */
export function Slider({
  value,
  min = 0,
  max,
  step = 1,
  keyStep,
  onInput,
  onCommit,
  label,
  valueText,
  disabled = false,
  className = '',
}: {
  value: number;
  min?: number;
  max: number;
  step?: number;
  /** Arrow-key step; defaults to `step`. */
  keyStep?: number;
  onInput?: (v: number) => void;
  onCommit: (v: number) => void;
  label: string;
  valueText?: string;
  disabled?: boolean;
  className?: string;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState<number | null>(null);
  const shown = Math.min(max, Math.max(min, drag ?? value));
  const pct = max > min ? ((shown - min) / (max - min)) * 100 : 0;

  const at = (clientX: number) => {
    const r = trackRef.current?.getBoundingClientRect();
    if (!r || r.width === 0) return shown;
    const f = Math.min(1, Math.max(0, (clientX - r.left) / r.width));
    const raw = min + f * (max - min);
    const snapped = Math.round(raw / step) * step;
    return Math.min(max, Math.max(min, Number(snapped.toFixed(6))));
  };

  const move = (v: number) => {
    setDrag(v);
    onInput?.(v);
  };

  return (
    <div
      className={`slider${drag !== null ? ' dragging' : ''}${disabled ? ' disabled' : ''} ${className}`}
      role="slider"
      tabIndex={disabled ? -1 : 0}
      aria-label={label}
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuenow={Math.round(shown * 100) / 100}
      aria-valuetext={valueText}
      aria-disabled={disabled || undefined}
      style={{ '--pct': `${pct}%` } as React.CSSProperties}
      onPointerDown={(e) => {
        if (disabled || (e.pointerType === 'mouse' && e.button !== 0)) return;
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);
        e.currentTarget.focus({ preventScroll: true });
        move(at(e.clientX));
      }}
      onPointerMove={(e) => {
        if (drag !== null) move(at(e.clientX));
      }}
      onPointerUp={(e) => {
        if (drag === null) return;
        const v = at(e.clientX);
        setDrag(null);
        onCommit(v);
      }}
      onPointerCancel={() => {
        if (drag === null) return;
        const v = drag;
        setDrag(null);
        onCommit(v);
      }}
      onKeyDown={(e) => {
        if (disabled) return;
        const k = keyStep ?? step;
        const big = (max - min) / 10;
        const next =
          e.key === 'ArrowRight' || e.key === 'ArrowUp'
            ? shown + k
            : e.key === 'ArrowLeft' || e.key === 'ArrowDown'
              ? shown - k
              : e.key === 'PageUp'
                ? shown + big
                : e.key === 'PageDown'
                  ? shown - big
                  : e.key === 'Home'
                    ? min
                    : e.key === 'End'
                      ? max
                      : null;
        if (next === null) return;
        e.preventDefault();
        onCommit(Math.min(max, Math.max(min, next)));
      }}
    >
      <div className="slider-track" ref={trackRef}>
        <div className="slider-fill" />
        <div className="slider-thumb" />
      </div>
    </div>
  );
}

const HUES = [12, 32, 280, 300, 330, 200, 160, 250];

/**
 * A person: their emoji if they chose one, otherwise initials on a soft tint
 * picked from their id. `ring` draws today's state around it - practised
 * (accent ring) or sitting right now (a slow breathing ring).
 */
export function Avatar({
  name,
  avatar,
  id,
  size = 40,
  ring = null,
}: {
  name: string;
  avatar: string | null;
  id: number;
  size?: number;
  ring?: 'done' | 'live' | 'open' | null;
}) {
  const initials =
    name
      .split(/\s+/)
      .map((w) => w[0])
      .join('')
      .slice(0, 2)
      .toUpperCase() || '?';
  const hue = HUES[Math.abs(id) % HUES.length];
  return (
    <span
      className={`avatar${ring ? ` ring-${ring}` : ''}`}
      style={
        {
          '--av': `${size}px`,
          '--av-hue': hue,
        } as React.CSSProperties
      }
      aria-hidden="true"
    >
      <span className="avatar-face">{avatar ?? initials}</span>
    </span>
  );
}
